import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useColumnResize } from "../hooks/useColumnResize";
import useTabelaPreferencia from "../hooks/useTabelaPreferencia";

const ColumnResizeContext = createContext(null);

/**
 * Provider que injeta o estado de resize de colunas no contexto, para que
 * filhos (`<ResizableTh>`, `<ResizableColgroup>`, `<ResetColumnsButton>`)
 * possam consumi-lo sem precisar receber tableId/columns por prop.
 *
 * Bloco TBL.2 (2026-05-25): quando `persistirNoBackend=true`, plugamos o
 * hook `useTabelaPreferencia` para carregar/salvar via backend
 * (cross-device) com fallback localStorage automatico. Quando
 * `persistirNoBackend=false` (default), comportamento legado: salva
 * localStorage direto (por-dispositivo).
 *
 * @param {Object} props
 * @param {string} props.tableId
 * @param {Array<{ key, defaultWidth? }>} props.columns
 * @param {boolean} [props.persistirNoBackend=false] - opt-in para usar
 *   provider de preferencias backend (cross-device). Tabelas migradas
 *   no Bloco TBL.5 passam true.
 */
export function ColumnResizeProvider({
  tableId,
  columns,
  persistirNoBackend = false,
  children,
}) {
  // Hook de preferencias backend: sempre montado (passa tableId mesmo se
  // persistirNoBackend=false — provider tolera; performance OK porque
  // re-leitura eh barata) MAS so passamos onPersist/widthsIniciais ao
  // useColumnResize quando persistirNoBackend=true. Isso evita
  // sobrescrever localStorage de tabelas nao-migradas.
  const pref = useTabelaPreferencia(tableId);

  const onPersist = useCallback(
    (novoMap) => {
      pref.salvarWidths(novoMap);
    },
    [pref]
  );

  const opts = useMemo(
    () =>
      persistirNoBackend
        ? { onPersist, widthsIniciais: pref.widths }
        : undefined,
    [persistirNoBackend, onPersist, pref.widths]
  );

  const api = useColumnResize(tableId, columns || [], opts);
  const value = useMemo(
    () => ({ ...api, columns: columns || [], tableId, persistirNoBackend }),
    [api, columns, tableId, persistirNoBackend]
  );
  return (
    <ColumnResizeContext.Provider value={value}>{children}</ColumnResizeContext.Provider>
  );
}

export function useColumnResizeContext() {
  return useContext(ColumnResizeContext);
}
